import useUserUiPreferenceStorageValue from "../../hooks/useUserUiPreferenceStorageValue.js";
import { userUiPreferenceStorage } from "../../lib/userUiPreferenceStorage.js";
import React from "react";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";

import { useGeotabLiveAlerts } from "../GeotabLiveAlerts.jsx";
import { THRESHOLD_CHOICES } from "../geotab/geotabPreferences.js";
import useGeotabTolerance, { setGeotabTolerance } from "../../hooks/useGeotabTolerance.js";
import { ALERT_DURATIONS, ALERT_PLACES, alertCoverage, alertsEnabled } from "../../utils/geotabLiveAlerts.js";
import DesktopAlertPermissionGuide from "./DesktopAlertPermissionGuide.jsx";
import { AlertDriverEditor, AlertDriversHeadline, AlertDriversView } from "./DispatchAlertDriversMenu.jsx";
import { Chip, SwitchItem } from "./DispatchDisplayMenuControls.jsx";
import DispatchHeaderActionMenu from "./DispatchHeaderActionMenu.jsx";
import { readPreferencesTarget } from "../../utils/preferencesDelegation.js";

// L'encadré des avis Windows s'ouvre par-dessus la page : y cliquer ne ferme pas le menu.
const keepMenuForGuide = (target) => Boolean(target?.closest?.("[data-desktop-permission-guide]"));

// Sections repliables du menu : choix conservé pour le compte des préférences.
const FOLDED_KEY = "grain.dispatch-display-menu.folded";
function useFoldedSections() {
  const storedSections = useUserUiPreferenceStorageValue("local", FOLDED_KEY);
  const [folded, setFolded] = React.useState(() => {
    try { return new Set(JSON.parse(userUiPreferenceStorage("local")?.getItem(FOLDED_KEY) || "[]")); } catch { return new Set(); }
  });
  React.useEffect(() => {
    try { setFolded(new Set(JSON.parse(storedSections || "[]"))); } catch { /* Valeur invalide : garder le menu courant. */ }
  }, [storedSections]);
  const toggle = React.useCallback((id) => setFolded((current) => {
    const next = new Set(current);
    if (!next.delete(id)) next.add(id);
    try { userUiPreferenceStorage("local")?.setItem(FOLDED_KEY, JSON.stringify([...next])); } catch { /* stockage indisponible */ }
    return next;
  }), []);
  return { folded, toggle };
}

// Titre cliquable avec sa flèche ; repliée, la section résume ses réglages à droite.
function FoldSection({ id, label, summary, folds, children }) {
  const open = !folds.folded.has(id);
  return (
    <div role="group" aria-label={label} className="dispatch-header-menu-section" data-dispatch-display-section={id}>
      <button type="button" role="menuitem" tabIndex={-1} data-keep-menu-open aria-expanded={open} onClick={() => folds.toggle(id)}
        className="dispatch-header-menu-label flex w-full items-center gap-1.5 rounded-[4px] text-left hover:bg-slate-50">
        <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={2.75} />
        <span className="shrink-0">{label}</span>
        {!open && summary ? <span className="ml-auto min-w-0 truncate text-[0.7rem] font-semibold normal-case tracking-normal text-slate-700">{summary}</span> : null}
      </button>
      {open ? children : null}
    </div>
  );
}

const desktopPermission = () => (typeof Notification === "undefined" ? "unsupported" : Notification.permission);

// Permission de Chrome pour les avis Windows, relue dès qu'elle change dans les réglages du site.
function useDesktopPermission() {
  const [permission, setPermission] = React.useState(desktopPermission);
  React.useEffect(() => {
    let status = null;
    const update = () => setPermission(desktopPermission());
    navigator.permissions?.query({ name: "notifications" }).then((result) => { status = result; status.onchange = update; }, () => {});
    window.addEventListener("focus", update);
    return () => {
      if (status) status.onchange = null;
      window.removeEventListener("focus", update);
    };
  }, []);
  return [permission, setPermission];
}

// Avis Windows : Chrome ne repose jamais la question après « Bloquer » ; un clic montre alors comment les réactiver.
function DesktopAlertSwitch({ display, onChange }) {
  const [permission, setPermission] = useDesktopPermission();
  const [help, setHelp] = React.useState(false);
  // Avant la question de Chrome : « intro » explique où elle va apparaître, « waiting » attend la réponse.
  const [guide, setGuide] = React.useState(null);
  const askChrome = () => {
    setGuide("waiting");
    void Notification.requestPermission().then((result) => {
      setGuide(null);
      setPermission(result);
      if (result === "granted") onChange("alert_desktop", true);
      else if (result === "denied") setHelp(true);
    }, () => setGuide(null));
  };
  // Réautorisés dans Chrome pendant que l'aide est ouverte : les avis Windows s'activent.
  React.useEffect(() => {
    if (!help || permission !== "granted") return;
    setHelp(false);
    if (!display.alert_desktop) onChange("alert_desktop", true);
  }, [help, permission, display.alert_desktop, onChange]);
  const change = (option, value) => {
    if (!value || permission === "granted") onChange(option, value);
    else if (permission === "denied") setHelp(true);
    else if (permission === "default") setGuide("intro");
  };
  const hint = permission === "unsupported" ? "Ce navigateur n'affiche pas d'avis Windows"
    : permission === "denied" ? "Bloqués dans Chrome : cliquez pour voir comment les réactiver"
    : "Quand le SaaS n'est pas la fenêtre active";
  return (
    <>
      <SwitchItem option="alert_desktop" checked={display.alert_desktop && permission === "granted"} onChange={change} label="Avis Windows" hint={hint} />
      {guide ? <DesktopAlertPermissionGuide waiting={guide === "waiting"} onContinue={askChrome} onCancel={() => setGuide(null)} /> : null}
      {help ? (
        <div role="note" data-dispatch-display-desktop-help
          className="mx-2 mb-1.5 rounded-[4px] border border-slate-200 border-l-[3px] border-l-[#c2410c] bg-white px-2.5 py-2 text-[0.74rem] leading-5 text-slate-700">
          <div className="flex items-center gap-2 font-extrabold text-slate-900">
            Chrome bloque les avis Windows de ce site
            <button type="button" aria-label="Fermer l'aide" onClick={() => setHelp(false)} className="ml-auto grid h-5 w-5 place-items-center rounded-[4px] text-slate-700 hover:bg-slate-100">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <ol className="m-0 mt-1 list-none space-y-0.5 p-0">
            <li><b>1.</b> Cliquez sur l'icône <span aria-hidden="true" className="inline-grid h-4 w-4 place-items-center rounded-[3px] border border-slate-300 align-[-3px]"><SlidersHorizontal className="h-2.5 w-2.5" /></span> à gauche de l'adresse du site.</li>
            <li><b>2.</b> Activez « Notifications ».</li>
            <li><b>3.</b> Revenez ici : les avis Windows s'activent tout seuls.</li>
          </ol>
        </div>
      ) : null}
    </>
  );
}

// Avis Geotab de la journée en cours : le réglage par défaut de tous les chauffeurs, et comment les recevoir.
function LiveAlertsSection({ display, onChange, error, folds }) {
  const summary = alertsEnabled(display) ? [display.alert_arrival && "Arrivée", display.alert_departure && "Terminé"].filter(Boolean).join(" · ") : "Éteints";
  return (
    <FoldSection id="alerts" label="Avis Geotab · aujourd'hui" summary={summary} folds={folds}>
      <SwitchItem option="alert_arrival" checked={display.alert_arrival} onChange={onChange} label="Arrivée" hint="Le camion arrive sur place" />
      <SwitchItem option="alert_departure" checked={display.alert_departure} onChange={onChange} label="Terminé" hint="Le camion repart" />
      <div role="group" aria-label="Lieux" className="flex items-center gap-1 px-2 py-1.5 text-[0.84rem]">
        <span className="mr-auto font-bold text-content-ink">Lieux</span>
        {ALERT_PLACES.map(({ option, label }) => {
          const checked = Boolean(display[option]);
          // Un lieu reste toujours choisi.
          const last = checked && ALERT_PLACES.every((place) => place.option === option || !display[place.option]);
          return <Chip key={option} option={option} checked={checked} disabled={last} label={label} onClick={() => onChange(option, !checked)} />;
        })}
      </div>
      <div role="group" aria-label="Durée à l'écran" className="px-2 py-1.5 text-[0.84rem]">
        <div className="flex items-center gap-1">
          <span className="mr-auto font-bold text-content-ink">Durée</span>
          {ALERT_DURATIONS.map(([seconds, label]) => (
            <Chip key={seconds} option="alert_seconds" value={seconds} checked={(display.alert_seconds ?? 20) === seconds} label={label}
              onClick={() => onChange("alert_seconds", seconds)} />
          ))}
        </div>
        <div className="text-[0.72rem] text-slate-700">{display.alert_seconds === 0 ? "La carte reste jusqu'à ce qu'on la ferme" : "Temps à l'écran ; la souris dessus la retient"}</div>
      </div>
      <SwitchItem option="alert_sound" checked={display.alert_sound} onChange={onChange} label="Petit son" hint="Un « ding » discret à chaque avis" />
      <DesktopAlertSwitch display={display} onChange={onChange} />
      <div className="px-2 pb-1 text-[0.72rem] text-slate-700" data-dispatch-display-alerts-status>
        {error && alertsEnabled(display) ? error : "Tournées d'aujourd'hui, sur toutes les pages. Hors plan : arrêt de 10 min et plus. Geotab : quelques minutes de délai."}
      </div>
    </FoldSection>
  );
}

function DisplayMenuContent({ isOpen, display, onChange, canSeeGeotab, geotabStatus, productTagsDefaultOff }) {
  const live = useGeotabLiveAlerts();
  const tolerance = useGeotabTolerance(canSeeGeotab);
  // « main », « drivers » (la liste) ou le chauffeur ouvert.
  const [view, setView] = React.useState("main");
  const folds = useFoldedSections();
  React.useEffect(() => { if (!isOpen) setView("main"); }, [isOpen]);
  if (canSeeGeotab && view === "drivers") {
    return <AlertDriversView display={display} onChange={onChange} live={live} onBack={() => setView("main")} onDriver={(driver) => setView(driver)} />;
  }
  if (canSeeGeotab && view && typeof view === "object") {
    return <AlertDriverEditor display={display} onChange={onChange} driver={view} onBack={() => setView("drivers")} />;
  }
  const realOn = display.geotab_progress || display.geotab_durations;
  return (
    <>
      {canSeeGeotab ? <AlertDriversHeadline display={display} drivers={live?.drivers} onOpen={() => setView("drivers")} /> : null}
      {canSeeGeotab ? <LiveAlertsSection display={display} onChange={onChange} error={live?.error} folds={folds} /> : null}
      {canSeeGeotab ? (
        <FoldSection id="real" label="Réel Geotab" folds={folds}
          summary={realOn ? [display.geotab_progress && "Avancement", display.geotab_durations && "Écarts", `± ${tolerance} min`].filter(Boolean).join(" · ") : "Éteint"}>
          <SwitchItem option="geotab_progress" checked={display.geotab_progress} onChange={onChange}
            label="Avancement" hint="✓ terminé · ● sur place, pas fini · ✕ pas vu. « 2/3 » : arrêts terminés, sans l'inspection ni la veille." />
          <SwitchItem option="geotab_durations" checked={display.geotab_durations} onChange={onChange}
            label="Écarts de durée" hint="Sur place, puis route vers l'étape suivante" />
          <div role="group" aria-label="Tolérance des écarts" className="px-2 py-1.5 text-[0.84rem]">
            <div className="flex items-center gap-1">
              <span className="mr-auto font-bold text-content-ink">Tolérance ±</span>
              {THRESHOLD_CHOICES.tolerance_min.map(([value, label]) => (
                <Chip key={value} option="geotab_tolerance" value={value} checked={tolerance === value} label={label.replace("± ", "")}
                  onClick={() => void setGeotabTolerance(value).catch(() => {})} />
              ))}
            </div>
            <div className="text-[0.72rem] text-slate-700">Écart en vert dans cette marge : Gantt, avis et section Geotab</div>
          </div>
          {realOn && geotabStatus ? <div className="px-2 pb-1 text-[0.72rem] text-slate-700" data-dispatch-display-geotab-status>{geotabStatus}</div> : null}
        </FoldSection>
      ) : null}
      <FoldSection id="gantt" label="Gantt" folds={folds} summary={display.product_tags ? "Étiquettes affichées" : "Étiquettes masquées aujourd'hui"}>
        <SwitchItem option="product_tags" checked={display.product_tags} onChange={onChange}
          label="Étiquettes de produits" hint={productTagsDefaultOff ? "Masquées par défaut avec Avancement. Choix conservé pour ce compte." : "Au-dessus des segments dans le Dispatch d'aujourd'hui. Choix conservé pour ce compte."} />
      </FoldSection>
    </>
  );
}

// Menu Affichage du Gantt : ce que le répartiteur garde à l'écran, et ses avis Geotab, enregistrés pour lui.
export default function DispatchDisplayMenu({ display, onChange, canSeeGeotab = false, geotabStatus = "", productTagsDefaultOff = false }) {
  const live = useGeotabLiveAlerts();
  const [preferencesTarget] = React.useState(readPreferencesTarget);
  if (!canSeeGeotab) return null;
  const geotabOn = canSeeGeotab && (display.geotab_progress || display.geotab_durations || alertsEnabled(display));
  // Chauffeurs du jour sans aucun avis : visible même menu fermé.
  const silenced = canSeeGeotab && alertsEnabled(display) ? alertCoverage(display, live?.drivers).none.length : 0;
  return (
    <DispatchHeaderActionMenu label={geotabOn ? "Affichage · Réel" : "Affichage"} icon={SlidersHorizontal}
      triggerClassName={`fleet-display-menu-trigger relative${geotabOn ? " is-geotab-on" : ""}`} menuClassName="dispatch-display-menu"
      shouldIgnoreTarget={keepMenuForGuide}
      triggerBadge={silenced ? (
        <span title={`${silenced} chauffeur${silenced > 1 ? "s" : ""} sans avis Geotab`} data-dispatch-display-silenced={silenced}
          className="absolute -right-1.5 -top-1.5 min-w-4 rounded-[4px] bg-[#c2410c] px-1 text-center text-[9.5px] font-black leading-4 text-white ring-2 ring-white">
          {silenced}
        </span>
      ) : null}>
      {({ isOpen }) => (
        <>
          {preferencesTarget ? (
            <p className="m-0 mb-1.5 rounded-[4px] bg-[#b45309] px-2 py-1 text-[0.75rem] leading-[1.35] text-white" data-preferences-delegation-reminder="">
              <b>Pour {preferencesTarget.email || preferencesTarget.userId}</b> · réglages enregistrés pour cet utilisateur
            </p>
          ) : null}
          <DisplayMenuContent isOpen={isOpen} display={display} onChange={onChange} canSeeGeotab={canSeeGeotab} geotabStatus={geotabStatus} productTagsDefaultOff={productTagsDefaultOff} />
        </>
      )}
    </DispatchHeaderActionMenu>
  );
}
